import { ColumnDef } from "@tanstack/react-table";

/**
 * Pin a column against the right edge while the rest of the table scrolls horizontally, so an
 * always-needed cell (a row's action menu, a status) stays reachable on a wide table.
 *
 * Opt-in per column via `sticky: "right"` on the column def — a table with no sticky column renders
 * exactly as before.
 */
export const readStickyColumn = <T extends object>(columnDef: ColumnDef<T>): "right" | undefined =>
  (columnDef as { sticky?: "right" }).sticky;

/**
 * The line separating the pinned column from the content sliding under it.
 *
 * A shadow rather than `border-l`, because Tailwind's preflight sets `border-collapse: collapse` on
 * every table. Under collapsed borders a cell's border belongs to the TABLE's border grid, not to the
 * cell's own box, so it stays at the column's natural position while the pinned cell moves away from
 * it — the line is there at the far-right of the scroll and gone everywhere else. A box-shadow is
 * painted by the cell itself, so it travels with it.
 *
 * `theme()` keeps the colour as a token instead of a copied hex; it resolves at build time to the same
 * `#D0D5DD` as `border-neutral-mid-gray` did.
 */
const STICKY_DIVIDER = "shadow-[inset_1px_0_0_0_theme(colors.neutral.mid-gray)]";

/**
 * A pinned cell is painted over the cells scrolling beneath it, so it MUST carry an opaque background
 * of its own — without one the text underneath shows straight through the pinned content.
 *
 * The background is spelled out here rather than inherited from the row. `bg-inherit` reads better and
 * would track any row colour automatically, but this project REPLACES `theme.colors` instead of
 * extending it (tailwind.config.cjs) and never re-declares `inherit`, so `bg-inherit` compiles to
 * nothing at all — silently, and invisibly to a test that only asserts the class is on the element.
 * Don't reintroduce it without adding `inherit` to the theme first.
 *
 * The hover colour is DataTable's own (`cardClass` sets `tr:hover` to the same value), so the pinned
 * cell stays in step with the row it belongs to instead of staying stubbornly white under the cursor.
 * It reacts via `group-hover`, which is why DataTableRow marks a row carrying a pinned cell as `group`.
 *
 * `!sticky` overrides the `relative` that DraggableRow bakes into every cell — Tailwind emits both
 * position utilities in one layer, so without the important flag which one wins depends on their order
 * in the stylesheet rather than on this call.
 *
 * z-index stays deliberately low: it only has to beat the sibling cells it covers, and BookingHistory's
 * in-flight overlay sits at z-10 above the whole table.
 */
export const STICKY_RIGHT_CELL = `!sticky right-0 z-[1] ${STICKY_DIVIDER} bg-white group-hover:bg-neutral-gray`;

/**
 * Header counterpart — one z-index above the body cells, so the corner belongs to the header when both
 * are pinned. `<th>` already carries an opaque `bg-neutral-surface-gray`, so it needs no background here.
 */
export const STICKY_RIGHT_HEADER = `!sticky right-0 z-[2] ${STICKY_DIVIDER}`;
